import { ActivityLevelValidation } from '@/api/EmissionReportModel'
import { UnitLabel } from '@/shared/config/config'
import useI18n from '@/shared/hooks/useI18n'
import { Button, DataBlock, GenericDialog, NumericInputGroup } from '@/common'
import { useMemo, useState } from 'react'

const initializeState = (initialValues: ActivityLevelValidation | null) => {
  return {
    changeInStock: initialValues?.changeInStock?.toString() || '',
    waste: initialValues?.waste?.toString() || ''
  }
}

export default function ActivityLevelValidationDialog({
  isOpen,
  close,
  onSave,
  initialValues,
  activityLevel,
  precursorAmmountInTonnes,
  preConsumerScrapAmmountInTonnes,
  otherMaterialsAmmountInTonnes
}: {
  isOpen: boolean
  close: () => void
  onSave: (payload: {
    hasToBeReEvaluated: boolean
    changeInStock: number
    conversions: number
    waste: number
    conversionsPercentage: number
  }) => void
  activityLevel: number
  precursorAmmountInTonnes: number
  preConsumerScrapAmmountInTonnes: number
  otherMaterialsAmmountInTonnes: number
  initialValues: ActivityLevelValidation | null
}) {
  const { t } = useI18n()
  const [state, setState] = useState<{
    changeInStock: string
    waste: string
  }>(initializeState(initialValues))

  const { changeInStock, waste } = state
  const inputValues =
    Number(precursorAmmountInTonnes) +
    Number(preConsumerScrapAmmountInTonnes) +
    Number(otherMaterialsAmmountInTonnes)

  const conversions = useMemo(() => {
    const changeInStockValue = Number(changeInStock)
    const wasteValue = Number(waste)

    const outputValues = changeInStockValue + wasteValue + activityLevel
    return (inputValues - outputValues).toFixed()
  }, [changeInStock, waste, activityLevel, inputValues])

  const updateState = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = evt.target
    setState(prev => ({ ...prev, [id]: value }))
  }

  const isSaveEnabled = useMemo(() => {
    return changeInStock && waste
  }, [changeInStock, waste])

  const onClose = () => {
    setState(initializeState(initialValues))
    close()
  }

  const onSavedClick = () => {
    onSave({
      hasToBeReEvaluated: false,
      changeInStock: Number(changeInStock),
      conversions: Number(conversions),
      conversionsPercentage: (Number(conversions) / Number(inputValues)) * 100 || 0,
      waste: Number(waste)
    })
    close()
  }

  return (
    <GenericDialog
      title={t('report:activityLevelValidation.dialog.title')}
      isOpen={isOpen}
      close={onClose}
      size="lg"
    >
      <div className="space-y-4 mb-6">
        <NumericInputGroup
          label={t('report:activityLevelValidation.dialog.fields.activityLevel.label')}
          value={activityLevel.toString()}
          disabled
          suffix={UnitLabel.t}
          numericFormat={{
            decimalScale: 3,
            allowNegative: false
          }}
        />
        <NumericInputGroup
          label={t('report:activityLevelValidation.dialog.fields.conversions.label')}
          disabled
          value={conversions}
          suffix={UnitLabel.t}
        />
        <NumericInputGroup
          label={t('report:activityLevelValidation.dialog.fields.changeInStock.label')}
          value={changeInStock}
          id="changeInStock"
          onChange={evt => updateState(evt)}
          suffix={UnitLabel.t}
          numericFormat={{
            decimalScale: 3,
            allowNegative: false
          }}
        />
        <NumericInputGroup
          label={t('report:activityLevelValidation.dialog.fields.waste.label')}
          value={waste}
          id="waste"
          suffix={UnitLabel.t}
          onChange={evt => updateState(evt)}
          numericFormat={{
            decimalScale: 3,
            allowNegative: false
          }}
        />
        <div className="grid grid-cols-3 gap-2">
          <DataBlock
            label={t('report:activityLevelValidation.dialog.dataBlocks.precursorQuantities.label')}
            data={`${precursorAmmountInTonnes} ${UnitLabel.t}`}
          />
          <DataBlock
            label={t('report:activityLevelValidation.dialog.dataBlocks.preConsumerScrap.label')}
            data={`${preConsumerScrapAmmountInTonnes} ${UnitLabel.t}`}
          />
          <DataBlock
            label={t('report:activityLevelValidation.dialog.dataBlocks.otherMaterials.label')}
            data={`${otherMaterialsAmmountInTonnes} ${UnitLabel.t}`}
          />
        </div>
      </div>
      <footer className="gap-2 flex justify-end">
        <Button variant="neutral" onClick={onClose} className="min-w-24">
          {t('common:actions.cancel')}
        </Button>
        <Button onClick={onSavedClick} disabled={!isSaveEnabled}>
          {t('common:actions.save')}
        </Button>
      </footer>
    </GenericDialog>
  )
}
